import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { API_BASE_URL } from '../../../config.js';
import Pagination from '../../Pagination/Paginations';
import EntriesSelector from '../../Pagination/EntriesSelector';
import {
	statisticsAction,
	statisticsCloseAction
} from '../../../actions/settingsAction';

/** A halfday is declared in whole hours, the way the school plans its day. */
const HOUR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

const EMPTY = {
	date: '',
	hours: '1',
	reason: '',
};

const Halfday = (props) => {
	const { fixNavbar } = props;

	const [formData, setFormData] = useState(EMPTY);
	const [user, setUser] = useState({ id: '', ...EMPTY });

	const [table, setTable] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);

	const [isSaving, setIsSaving] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const savingRef = useRef(false);
	const updatingRef = useRef(false);

	// clicked once the update lands, so the dialog closes through Bootstrap's own
	// dismiss handler rather than needing jQuery on the window
	const editCloseRef = useRef(null);

	const authHeader = {
		headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
	};

	// the list reads better day-first, the way dates are written on the rest of
	// the HRMS; the API keeps storing yyyy-MM-dd
	const formatDate = (value) => {
		if (!value) return '';

		const [year, month, day] = String(value).split('-');
		return day && month && year ? `${day}-${month}-${year}` : value;
	};

	const notify = (status, message) => {
		const options = {
			position: 'bottom-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'colored',
		};

		if (status === 'success') toast.success(message, options);
		else toast.error(message, { ...options, toastId: 'halfday' });
	};

	const loadHalfday = () => {
		axios
			.get(API_BASE_URL + '/gethalfday', authHeader)
			.then((res) => setTable(res.data.data.halfday || []))
			.catch((err) => console.log(err));
	};

	useEffect(() => {
		loadHalfday();
	}, []);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handelInput = (e) => {
		const { name, value } = e.target;
		setUser({ ...user, [name]: value });
	};

	// both date spellings are searchable, so typing what the row shows still finds it
	const filteredItems = table.filter((item) =>
		`${item.date || ''} ${formatDate(item.date)} ${item.hours || ''} ${item.reason || ''}`
			.toLowerCase()
			.includes(searchTerm.toLowerCase())
	);

	const startIndex = (currentPage - 1) * itemsPerPage;
	const filteredData = filteredItems.slice(startIndex, startIndex + itemsPerPage);
	const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

	const handleSubmit = (e) => {
		e.preventDefault();

		// block repeat submits while a save request is already running
		if (savingRef.current) return;

		savingRef.current = true;
		setIsSaving(true);

		axios
			.post(API_BASE_URL + '/add_halfday', formData, authHeader)
			.then((response) => {
				notify(response.data.status, response.data.message);

				if (response.data.status === 'success') {
					setFormData(EMPTY);
					loadHalfday();
				}
			})
			.catch((error) => {
				notify('error', error.response?.data?.message || 'Could not add the halfday');
			})
			.finally(() => {
				savingRef.current = false;
				setIsSaving(false);
			});
	};

	const handleUpdate = (id) => {
		axios
			.get(API_BASE_URL + '/showhalfday/' + id, authHeader)
			.then((res) => setUser(res.data))
			.catch((err) => console.log(err));
	};

	const handleEditSubmit = (e) => {
		e.preventDefault();

		// block repeat submits while an update request is already running
		if (updatingRef.current) return;

		updatingRef.current = true;
		setIsUpdating(true);

		axios
			.post(
				API_BASE_URL + '/updatehalfday',
				{ id: user.id, date: user.date, hours: user.hours, reason: user.reason },
				authHeader
			)
			.then((response) => {
				notify(response.data.status, response.data.message);

				if (response.data.status === 'success') {
					editCloseRef.current?.click();
					loadHalfday();
				}
			})
			.catch((error) => {
				notify('error', error.response?.data?.message || 'Could not update the halfday');
			})
			.finally(() => {
				updatingRef.current = false;
				setIsUpdating(false);
			});
	};

	const handleDelete = (id) => {
		if (!window.confirm('Delete this halfday?')) return;

		axios
			.post(API_BASE_URL + '/deletehalfday', { id }, authHeader)
			.then((response) => {
				notify(response.data.status, response.data.message);

				if (response.data.status === 'success') loadHalfday();
			})
			.catch((error) => {
				notify('error', error.response?.data?.message || 'Could not delete the halfday');
			});
	};

	return (
		<>
			<div className={`section-body ${fixNavbar ? 'marginTop' : ''} `}>
				<div className="container-fluid">
					<ToastContainer />

					<div className="row clearfix">
						{/* the form sits beside the list, the way the halfday screen is laid
						    out on the rest of the HRMS */}
						<div className="col-lg-4 col-md-12">
							<div className="card">
								<div className="card-header">
									<h3 className="card-title">Add New Halfday</h3>
								</div>

								<form onSubmit={handleSubmit}>
									<div className="card-body">
										<div className="form-group required-tooltip">
											<label>Select Date</label>
											<DatePicker
												selected={formData.date ? new Date(formData.date) : null}
												onChange={(date) =>
													setFormData({
														...formData,
														date: date ? date.toLocaleDateString('en-CA') : '',
													})
												}
												dateFormat="dd-MM-yyyy"
												placeholderText="Select Date"
												className="form-control required-input"
											/>
										</div>

										<div className="form-group required-tooltip">
											<label>Select Hours</label>
											<select
												className="form-control custom-select required-select"
												name="hours"
												value={formData.hours}
												onChange={handleChange}
											>
												{HOUR_OPTIONS.map((hour) => (
													<option key={hour} value={hour}>{hour}</option>
												))}
											</select>
										</div>

										<div className="form-group required-tooltip">
											<label>Reason</label>
											<textarea
												rows={4}
												className="form-control required-input"
												placeholder="Reason"
												name="reason"
												value={formData.reason}
												onChange={handleChange}
											/>
										</div>

										<button type="submit" className="btn btn-primary" disabled={isSaving}>
											<i className="fa fa-check-square-o mr-1" />
											{isSaving ? 'Saving...' : 'Save'}
										</button>
									</div>
								</form>
							</div>
						</div>

						<div className="col-lg-8 col-md-12">
							<div className="card">
								<div className="card-header">
									<h3 className="card-title">Halfday List</h3>

									<div className="card-options">
										<div
											className="input-group"
											style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}
										>
											<div style={{ marginRight: '15px' }}>
												<EntriesSelector
													entriesPerPage={itemsPerPage}
													setEntriesPerPage={setItemsPerPage}
													setCurrentPage={setCurrentPage}
												/>
											</div>

											<div style={{ position: 'relative', width: '220px' }}>
												<input
													type="text"
													className="form-control form-control-sm"
													placeholder="Search"
													value={searchTerm}
													onChange={(e) => setSearchTerm(e.target.value)}
													style={{
														width: '100%',
														height: '30px',
														padding: '3px 8px',
														paddingRight: '30px',
														fontSize: '13px',
														boxSizing: 'border-box',
													}}
												/>

												{searchTerm.length > 0 && (
													<span
														onClick={() => setSearchTerm('')}
														style={{
															position: 'absolute',
															top: '50%',
															right: '10px',
															transform: 'translateY(-50%)',
															cursor: 'pointer',
															color: '#aaa',
															fontSize: '18px',
														}}
													>
														✖
													</span>
												)}
											</div>
										</div>
									</div>
								</div>

								<div className="card-body">
									<div className="table-responsive">
										<table className="table table-hover table-striped table-vcenter mb-0">
											<thead>
												<tr>
													<th style={{ width: '70px' }}>Action</th>
													<th>Date</th>
													<th style={{ width: '90px' }}>Hours</th>
													<th>Reason</th>
												</tr>
											</thead>
											<tbody>
												{filteredData.length === 0 && (
													<tr>
														<td colSpan={4} className="text-center text-muted">
															No halfday added yet
														</td>
													</tr>
												)}

												{filteredData.map((row) => (
													<tr key={row.id}>
														<td className="text-nowrap">
															<button
																type="button"
																onClick={() => handleUpdate(row.id)}
																className="btn btn-icon btn-sm"
																title="Edit"
																data-toggle="modal"
																data-target="#halfdayEditModal"
															>
																<i className="fa fa-edit text-primary" />
															</button>

															<button
																type="button"
																onClick={() => handleDelete(row.id)}
																className="btn btn-icon btn-sm"
																title="Delete"
															>
																<i className="fa fa-trash-o text-danger" />
															</button>
														</td>
														<td className="text-nowrap">{formatDate(row.date)}</td>
														<td>
															<span className="badge badge-primary">{row.hours}</span>
														</td>
														<td>{row.reason}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>

									<Pagination
										currentPage={currentPage}
										totalPages={totalPages}
										onPageChange={setCurrentPage}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				className="modal fade"
				id="halfdayEditModal"
				tabIndex={-1}
				role="dialog"
				aria-labelledby="halfdayEditModalLabel"
				aria-hidden="true"
			>
				<form onSubmit={handleEditSubmit}>
					<div className="modal-dialog" role="document">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title" id="halfdayEditModalLabel">
									Edit Halfday
								</h5>
								<button
									type="button"
									className="close"
									data-dismiss="modal"
									aria-label="Close"
								>
									<span aria-hidden="true">×</span>
								</button>
							</div>

							<div className="modal-body">
								<div className="row clearfix">
									<div className="col-md-6 col-sm-6">
										<div className="form-group required-tooltip">
											<label>Select Date</label>
											<DatePicker
												selected={user.date ? new Date(user.date) : null}
												onChange={(date) =>
													setUser({
														...user,
														date: date ? date.toLocaleDateString('en-CA') : '',
													})
												}
												dateFormat="dd-MM-yyyy"
												placeholderText="Select Date"
												className="form-control required-input"
											/>
										</div>
									</div>

									<div className="col-md-6 col-sm-6">
										<div className="form-group required-tooltip">
											<label>Select Hours</label>
											<select
												className="form-control custom-select required-select"
												name="hours"
												value={user.hours || ''}
												onChange={handelInput}
											>
												{HOUR_OPTIONS.map((hour) => (
													<option key={hour} value={hour}>{hour}</option>
												))}
											</select>
										</div>
									</div>

									<div className="col-md-12 col-sm-12">
										<div className="form-group required-tooltip">
											<label>Reason</label>
											<textarea
												rows={3}
												className="form-control required-input"
												name="reason"
												value={user.reason || ''}
												onChange={handelInput}
											/>
										</div>
									</div>
								</div>
							</div>

							<div className="modal-footer">
								<button
									type="button"
									className="btn btn-secondary"
									data-dismiss="modal"
									ref={editCloseRef}
								>
									Close
								</button>
								<button type="submit" className="btn btn-primary" disabled={isUpdating}>
									{isUpdating ? 'Saving...' : 'Save'}
								</button>
							</div>
						</div>
					</div>
				</form>
			</div>
		</>
	);
};

const mapStateToProps = (state) => ({
	fixNavbar: state.settings.isFixNavbar,
	statisticsOpen: state.settings.isStatistics,
	statisticsClose: state.settings.isStatisticsClose,
});

const mapDispatchToProps = (dispatch) => ({
	statisticsAction: (e) => dispatch(statisticsAction(e)),
	statisticsCloseAction: (e) => dispatch(statisticsCloseAction(e)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Halfday);
